import axios from 'axios'
import { ObjectLoader, type BufferAttribute, type BufferGeometry, type Mesh, type Object3D, type Scene, type SceneJSON } from 'three'
import { ThreeItemKeys } from '../constants/three'
import { decompress } from '../helpers/files'
import { getSceneLights, isSceneLights } from '../helpers/lights'
import { parse } from '../helpers/scene-json'
import { restoreOriginalMaterial } from '../helpers/selection-material'

export default class ScenePersistence {
  public static async onLoad(url: string): Promise<Scene> {
    const stringifiedSceneJson = await this.fetchAndDecompressSceneJson(url)
    const loader = new ObjectLoader()
    const parsedScene = loader.parse(parse(stringifiedSceneJson)) as Scene

    this.resetLights(parsedScene)
    this.removeTransformControlsFromScene(parsedScene)
    this.recomputeMeshNormals(parsedScene)
    restoreOriginalMaterial(parsedScene)

    return parsedScene
  }

  public static prepareForSave(scene: Scene): SceneJSON {
    const detached = this.detachTransientObjects(scene)
    // Render-only attributes (normals/uv) are not needed by the slicer, so strip them
    // before toJSON to roughly halve the payload. Normals are recomputed on load
    // (`recomputeMeshNormals`). NOTE: UVs are dropped permanently — they are NOT
    // recomputed, so a save/load round-trip loses them. Safe today because every
    // material is solid-colour (no texture map uses UVs); if a UV-dependent material
    // is ever introduced, it must be re-derived on load or excluded from stripping.
    const stripped = this.stripRenderAttributes(scene)

    const jsonScene = scene.toJSON()

    stripped.forEach(({ geometry, name, attribute }) => geometry.setAttribute(name, attribute))
    detached.forEach(({ obj, parent }) => parent.add(obj))

    if (import.meta.env.VITE_LOG_JSON === 'true') {
      console.info(jsonScene)
    }

    return jsonScene
  }

  private static stripRenderAttributes(
    scene: Scene,
  ): { geometry: BufferGeometry, name: string, attribute: BufferAttribute }[] {
    const stripped: { geometry: BufferGeometry, name: string, attribute: BufferAttribute }[] = []

    scene.traverse((obj) => {
      const geometry = (obj as Mesh).geometry
      if (!(obj as Mesh).isMesh || !geometry?.isBufferGeometry) {
        return
      }
      for (const name of ['normal', 'uv']) {
        const attribute = geometry.getAttribute(name) as BufferAttribute | undefined
        if (attribute) {
          stripped.push({ geometry, name, attribute })
          geometry.deleteAttribute(name)
        }
      }
    })

    return stripped
  }

  private static recomputeMeshNormals(scene: Object3D): void {
    scene.traverse((obj) => {
      const geometry = (obj as Mesh).geometry
      if (
        (obj as Mesh).isMesh
        && geometry?.isBufferGeometry
        && geometry.getAttribute('position')
        && !geometry.getAttribute('normal')
      ) {
        geometry.computeVertexNormals()
      }
    })
  }

  private static async fetchAndDecompressSceneJson(url: string) {
    const { data } = await axios.get(url, {
      responseType: 'arraybuffer',
      decompress: true,
    })
    const stringifiedSceneJson = await decompress(data)

    return stringifiedSceneJson
  }

  private static detachTransientObjects(scene: Scene): { obj: Object3D, parent: Object3D }[] {
    const transient: { obj: Object3D, parent: Object3D }[] = []
    scene.traverse((obj) => {
      if (obj.userData[ThreeItemKeys.TRANSIENT]) {
        transient.push({ obj, parent: obj.parent! })
      }
    })
    transient.forEach(({ obj, parent }) => parent.remove(obj))
    return transient
  }

  private static resetLights(scene: Scene): void {
    const childrenToRemove = []

    for (const child of scene.children) {
      if (isSceneLights(child)) {
        childrenToRemove.push(child)
      }
    }

    for (const child of childrenToRemove) {
      scene.remove(child)
    }

    scene.add(getSceneLights())
  }

  private static removeTransformControlsFromScene(scene: Scene): void {
    const itemsToDelete: Object3D[] = []
    const transformControlsNames = ['X', 'Y', 'Z', 'XY', 'XZ', 'YZ']
    scene.traverseVisible((item) => {
      if (transformControlsNames.includes(item.name)) {
        itemsToDelete.push(item)
      }
    })
    itemsToDelete.forEach(item => item.parent?.remove(item))
  }
}
